
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Locale = 'en' | 'sw';

interface LocalizationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  isLoading: boolean;
  availableLocales: { code: Locale; name: string; nativeName: string }[];
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: React.ReactNode;
  defaultLocale?: Locale;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  defaultLocale = 'en'
}) => {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);
  const [isLoading, setIsLoading] = useState(false);

  const availableLocales = [
    { code: 'en' as Locale, name: 'English', nativeName: 'English' },
    { code: 'sw' as Locale, name: 'Kiswahili', nativeName: 'Kiswahili' }
  ];

  const setLocale = async (newLocale: Locale) => {
    if (newLocale === locale) return;
    
    setIsLoading(true);
    try {
      await i18n.changeLanguage(newLocale);
      setLocaleState(newLocale);
      localStorage.setItem('preferred-locale', newLocale);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initialize locale from localStorage or browser preference
    const savedLocale = localStorage.getItem('preferred-locale') as Locale;
    const browserLocale = navigator.language.split('-')[0] as Locale;
    
    const initialLocale = savedLocale || 
      (availableLocales.some(l => l.code === browserLocale) ? browserLocale : defaultLocale);
    
    if (initialLocale !== locale) {
      setLocale(initialLocale);
    }
    
    // Also update i18n if it's different
    if (i18n.language !== initialLocale) {
      i18n.changeLanguage(initialLocale);
    }
  }, []);

  useEffect(() => {
    // Sync with i18n language changes
    const handleLanguageChange = (lng: string) => {
      const newLocale = lng as Locale;
      if (availableLocales.some(l => l.code === newLocale) && newLocale !== locale) {
        setLocaleState(newLocale);
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, [i18n, locale]);

  const value: LocalizationContextType = {
    locale,
    setLocale,
    isLoading,
    availableLocales
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};
